import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Copy, KeyRound, Mail, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ErrorState } from '@/components/ui/error-state';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast';
import { ApiError, request } from '@/lib/api';
import { formatRelative } from '@/lib/utils';

/* ---------------------------------------------------------------------------
   קליטת מיילים בהעברה אוטומטית (ADR 0001).

   במקום לתת לנו גישה לכל תיבת הדואר, בעל העסק מעביר אלינו רק את מה
   שהוא בוחר. ההגדרה ב-Gmail היא שלושה צעדים, והצעד היחיד שמבלבל —
   קוד האימות ש-Gmail שולח — מגיע אלינו ומוצג כאן, במקום להיעלם.
   --------------------------------------------------------------------------- */

export const mailboxSchema = z.object({
  enabled: z.boolean(),
  address: z.string(),
  verificationCode: z.string().nullable(),
  verificationUrl: z.string().nullable(),
  verificationReceivedAt: z.string().nullable(),
  lastReceivedAt: z.string().nullable(),
  receivedCount: z.number().int(),
});
export type Mailbox = z.infer<typeof mailboxSchema>;

const MAILBOX_KEY = ['intake', 'mailbox'];

export function EmailForwardingCard() {
  const { t } = useTranslation(['settings', 'common']);
  const toast = useToast();
  const qc = useQueryClient();
  const [confirmRotate, setConfirmRotate] = useState(false);

  const mailbox = useQuery({
    queryKey: MAILBOX_KEY,
    queryFn: ({ signal }) => request('/intake/mailbox', { schema: mailboxSchema, signal }),
    // עד שמגיע המייל הראשון בעל העסק נמצא באמצע ההגדרה ב-Gmail, ומחכה לקוד.
    // רענון תכוף רק אז; אחרי שהקליטה עובדת — אין סיבה.
    refetchInterval: (q) => (q.state.data && q.state.data.receivedCount === 0 ? 10_000 : false),
  });

  const rotate = useMutation({
    mutationFn: () => request('/intake/mailbox/rotate', { method: 'POST', schema: mailboxSchema }),
    onSuccess: (m) => {
      qc.setQueryData(MAILBOX_KEY, m);
      setConfirmRotate(false);
      toast.success(t('email.rotated'), t('email.rotatedHint'));
    },
    onError: (e) => toast.error(t('email.rotateFailed'), e instanceof ApiError ? e.message : undefined),
  });

  const copy = async (text: string, copied: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(copied);
    } catch {
      toast.error(t('email.copyBlocked'));
    }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div className="flex gap-3">
          <div className="grid size-9 shrink-0 place-items-center rounded-(--radius-md) bg-accent-subtle text-accent">
            <Mail className="size-4" aria-hidden />
          </div>
          <div>
            <CardTitle>{t('email.title')}</CardTitle>
            <CardDescription>{t('email.description')}</CardDescription>
          </div>
        </div>
        {mailbox.data ? <Status m={mailbox.data} /> : null}
      </CardHeader>

      <CardContent className="space-y-5">
        {mailbox.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : mailbox.isError ? (
          <ErrorState error={mailbox.error} onRetry={() => void mailbox.refetch()} />
        ) : mailbox.data ? (
          <>
            {!mailbox.data.enabled ? (
              <p className="rounded-lg border border-warning-border bg-warning-subtle px-3 py-2 text-sm text-warning">
                {t('email.notEnabled')}
              </p>
            ) : null}

            <div>
              <p className="mb-1.5 text-xs font-medium text-fg-muted">{t('email.yourAddress')}</p>
              <div className="flex items-center gap-2">
                <code
                  dir="ltr"
                  className="min-w-0 flex-1 truncate rounded-lg border border-border bg-surface-sunken px-3 py-2.5 text-start font-mono text-sm text-fg"
                >
                  {mailbox.data.address}
                </code>
                <Button variant="secondary" onClick={() => void copy(mailbox.data.address, t('email.copiedAddress'))}>
                  <Copy aria-hidden />
                  {t('email.copy')}
                </Button>
              </div>
            </div>

            {mailbox.data.verificationCode ? (
              <div className="rounded-xl border border-accent-border bg-accent-subtle p-4">
                <p className="flex items-center gap-2 text-sm font-medium text-fg">
                  <KeyRound className="size-4 text-accent" aria-hidden />
                  {t('email.codeTitle')}
                  {mailbox.data.verificationReceivedAt ? (
                    <span className="text-xs font-normal text-fg-muted">· {formatRelative(mailbox.data.verificationReceivedAt)}</span>
                  ) : null}
                </p>
                <div className="mt-2 flex items-center gap-3">
                  <span dir="ltr" className="tabular font-mono text-2xl font-semibold tracking-widest text-fg">
                    {mailbox.data.verificationCode}
                  </span>
                  <Button size="sm" variant="secondary" onClick={() => void copy(mailbox.data.verificationCode!, t('email.copiedCode'))}>
                    <Copy aria-hidden />
                    {t('email.copy')}
                  </Button>
                </div>
                <p className="mt-2 text-xs text-fg-muted">{t('email.codeHint')}</p>
              </div>
            ) : null}

            <Steps m={mailbox.data} />

            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
              <p className="text-xs text-fg-subtle">{t('email.spamHint')}</p>
              <Button size="sm" variant="ghost" onClick={() => setConfirmRotate(true)}>
                <RefreshCw aria-hidden />
                {t('email.newAddress')}
              </Button>
            </div>
          </>
        ) : null}
      </CardContent>

      <Dialog open={confirmRotate} onOpenChange={setConfirmRotate}>
        <DialogContent aria-describedby="rotate-desc">
          <DialogHeader>
            <DialogTitle>{t('email.rotateTitle')}</DialogTitle>
            <DialogDescription id="rotate-desc">
              {t('email.rotateDescription')}
            </DialogDescription>
          </DialogHeader>
          <DialogBody />
          <DialogFooter>
            <Button variant="secondary" onClick={() => setConfirmRotate(false)}>
              {t('common:actions.cancel')}
            </Button>
            <Button variant="danger" loading={rotate.isPending} onClick={() => rotate.mutate()}>
              {t('email.newAddress')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function Status({ m }: { m: Mailbox }) {
  const { t } = useTranslation('settings');
  if (m.lastReceivedAt) {
    return <Badge tone="success">{t('email.statusActive', { when: formatRelative(m.lastReceivedAt) })}</Badge>;
  }
  if (m.verificationCode) return <Badge tone="info">{t('email.statusWaiting')}</Badge>;
  return <Badge tone="neutral">{t('email.statusNotSet')}</Badge>;
}

/** שלושת הצעדים ב-Gmail. צעד שהושלם מסומן — כך רואים איפה עומדים. */
function Steps({ m }: { m: Mailbox }) {
  const { t } = useTranslation('settings');
  const steps = [
    {
      done: m.verificationCode !== null || m.receivedCount > 0,
      title: t('email.step1Title'),
      body: t('email.step1Body'),
    },
    {
      done: m.receivedCount > 0,
      title: t('email.step2Title'),
      body: t('email.step2Body'),
    },
    {
      done: m.receivedCount > 0,
      title: t('email.step3Title'),
      body: t('email.step3Body'),
    },
  ];

  return (
    <ol className="space-y-3">
      {steps.map((s, i) => (
        <li key={s.title} className="flex gap-3">
          {s.done ? (
            <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-success" aria-hidden />
          ) : (
            <span className="tabular grid size-5 shrink-0 place-items-center rounded-full border border-border text-2xs text-fg-muted">
              {i + 1}
            </span>
          )}
          <div>
            <p className={s.done ? 'text-sm font-medium text-fg-muted' : 'text-sm font-medium text-fg'}>{s.title}</p>
            <p className="mt-0.5 text-xs text-fg-muted">{s.body}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}
